import { FC, FocusEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { Link, useNavigate } from 'react-router-dom'
import { useLocation } from 'react-router'
import { ToggleComponent } from '../../../assets/ts/components'
import { KTSVG, MenuPaths } from '../../../helpers'
import { AsideNavFlyout, flyoutDomId } from './AsideNavFlyout'
import { NavSubLink } from './NavSubLink'

export type NavItem = {
  to: string
  title: string
  visible?: boolean
}

export type NavEntry =
  | { kind: 'link'; id: string; to: string; title: string; icon: MenuPaths; visible?: boolean }
  | {
      kind: 'group'
      id: string
      title: string
      icon: MenuPaths
      items: NavItem[]
      visible?: boolean
    }

export type NavSection = {
  id: string
  title: string
  entries: NavEntry[]
  visible?: boolean
}

const isVisible = (item: { visible?: boolean }) => item.visible !== false

const matches = (pathname: string, to: string) =>
  pathname === to || (to !== '/' && pathname.startsWith(`${to}/`))

// O menu recolhido é o mesmo estado do botão do header (ToggleComponent → atributo no body).
const isAsideMinimized = () => document.body.getAttribute('data-kt-aside-minimize') === 'on'

const expandAside = () => {
  const toggle = document.getElementById('kt_aside_toggle')
  const instance = toggle ? (ToggleComponent.getInstance(toggle) as ToggleComponent | null) : null
  if (instance) {
    instance.disable()
  } else {
    document.body.removeAttribute('data-kt-aside-minimize')
  }
}

// Busca sem diferenciar acento nem maiúscula ("relatorio" acha "Relatórios")
const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

// Grupo cujo título casa mostra todos os itens; senão, só os itens que casam.
const filterSections = (sections: NavSection[], query: string): NavSection[] => {
  if (!query) return sections

  return sections
    .map((section) => ({
      ...section,
      entries: section.entries
        .map((entry): NavEntry => {
          if (entry.kind === 'link' || normalize(entry.title).includes(query)) return entry
          return {
            ...entry,
            items: entry.items.filter((item) => normalize(item.title).includes(query)),
          }
        })
        .filter((entry) =>
          entry.kind === 'group' ? entry.items.length > 0 : normalize(entry.title).includes(query)
        ),
    }))
    .filter((section) => section.entries.length > 0)
}

// Campo de texto rico (Quill etc.) usa Ctrl+K para inserir link: o atalho da busca não o rouba
const isRichTextTarget = (target: EventTarget | null) =>
  target instanceof Element && !!target.closest('[contenteditable]:not([contenteditable="false"])')

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

// Trilho de ícones = menu recolhido no desktop (no mobile o aside é drawer e não recolhe)
const DESKTOP_QUERY = '(min-width: 992px)'
const readIsRail = () => isAsideMinimized() && window.matchMedia(DESKTOP_QUERY).matches

const useIsRail = () => {
  const [isRail, setIsRail] = useState(readIsRail)

  useEffect(() => {
    const update = () => setIsRail(readIsRail())
    const observer = new MutationObserver(update)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-kt-aside-minimize'],
    })
    const media = window.matchMedia(DESKTOP_QUERY)
    media.addEventListener?.('change', update)
    update()

    return () => {
      observer.disconnect()
      media.removeEventListener?.('change', update)
    }
  }, [])

  return isRail
}

const FLYOUT_CLOSE_DELAY_MS = 150

type FlyoutState = {
  entryId: string
  trigger: HTMLElement
  anchor: DOMRect
  focusFirst: boolean
}

type Props = {
  sections: NavSection[]
}

const AsideNav: FC<Props> = ({ sections }) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const searchRef = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState('')
  const isRail = useIsRail()
  const [flyout, setFlyout] = useState<FlyoutState | null>(null)
  const closeTimer = useRef<number>()

  const visibleSections = sections
    .filter(isVisible)
    .map((section) => ({
      ...section,
      entries: section.entries
        .filter(isVisible)
        .map((entry) =>
          entry.kind === 'group' ? { ...entry, items: entry.items.filter(isVisible) } : entry
        )
        .filter((entry) => entry.kind !== 'group' || entry.items.length > 0),
    }))
    .filter((section) => section.entries.length > 0)

  // Item ativo = o `to` mais específico que casa com a rota (ex.: /financeiro/faturas/responsavel
  // não acende também /financeiro/faturas).
  const allTargets = visibleSections.flatMap((section) =>
    section.entries.flatMap((entry): { to: string; groupId?: string }[] =>
      entry.kind === 'group'
        ? entry.items.map((item) => ({ to: item.to, groupId: entry.id }))
        : [{ to: entry.to }]
    )
  )
  const active = allTargets
    .filter((target) => matches(pathname, target.to))
    .sort((a, b) => b.to.length - a.to.length)[0]
  const activeTo = active?.to
  const activeGroupId = active?.groupId

  const [openGroupId, setOpenGroupId] = useState<string | undefined>(activeGroupId)

  useEffect(() => {
    setOpenGroupId(activeGroupId)
  }, [activeGroupId])

  // Ao abrir um resultado, a busca é limpa e o menu volta ao estado normal
  useEffect(() => {
    setSearch('')
  }, [pathname])

  const cancelFlyoutClose = useCallback(() => window.clearTimeout(closeTimer.current), [])

  const closeFlyout = useCallback(() => {
    cancelFlyoutClose()
    setFlyout(null)
  }, [cancelFlyoutClose])

  // Pequeno atraso para o mouse conseguir ir do ícone até o painel
  const scheduleFlyoutClose = useCallback(() => {
    cancelFlyoutClose()
    closeTimer.current = window.setTimeout(() => setFlyout(null), FLYOUT_CLOSE_DELAY_MS)
  }, [cancelFlyoutClose])

  const openFlyout = (entryId: string, trigger: HTMLElement, focusFirst = false) => {
    if (!isRail) return
    cancelFlyoutClose()
    setFlyout({ entryId, trigger, anchor: trigger.getBoundingClientRect(), focusFirst })
  }

  const dismissFlyout = () => {
    const trigger = flyout?.trigger
    closeFlyout()
    trigger?.focus()
  }

  // Painel fecha ao navegar, ao expandir o menu e quando a lista rola (a posição ficaria velha)
  useEffect(() => {
    closeFlyout()
  }, [pathname, isRail, closeFlyout])

  useEffect(() => {
    if (!flyout) return
    const handleScroll = () => closeFlyout()
    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [flyout, closeFlyout])

  useEffect(() => () => window.clearTimeout(closeTimer.current), [])

  // Foco saindo do ícone para fora do painel fecha o painel
  const handleTriggerBlur = (event: FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget as Node | null
    const panel = flyout && document.getElementById(flyoutDomId(flyout.entryId))
    if (next && panel?.contains(next)) return
    closeFlyout()
  }

  const query = normalize(search)
  const isSearching = query.length > 0
  const shownSections = filterSections(visibleSections, query)
  const firstResult = shownSections
    .flatMap((section) => section.entries)
    .map((entry) => (entry.kind === 'group' ? entry.items[0]?.to : entry.to))[0]

  // Ctrl/⌘+K foca a busca de qualquer tela (expandindo o menu se estiver recolhido)
  useEffect(() => {
    const handleShortcut = (event: globalThis.KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'k') return
      if (event.defaultPrevented || isRichTextTarget(event.target)) return

      event.preventDefault()
      if (isAsideMinimized()) expandAside()
      searchRef.current?.focus()
    }

    window.addEventListener('keydown', handleShortcut)
    return () => window.removeEventListener('keydown', handleShortcut)
  }, [])

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setSearch('')
    }
    if (event.key === 'Enter' && firstResult) {
      event.preventDefault()
      setSearch('')
      navigate(firstResult)
    }
  }

  const handleGroupClick = (groupId: string, trigger: HTMLElement) => {
    // Recolhido no desktop: abre o painel e leva o foco para dentro dele
    if (isRail) {
      if (flyout?.entryId === groupId && flyout.focusFirst) {
        closeFlyout()
      } else {
        openFlyout(groupId, trigger, true)
      }
      return
    }
    if (isAsideMinimized()) {
      expandAside()
      setOpenGroupId(groupId)
      return
    }
    setOpenGroupId((current) => (current === groupId ? undefined : groupId))
  }

  const flyoutEntry = flyout
    ? shownSections
        .flatMap((section) => section.entries)
        .find((entry) => entry.id === flyout.entryId)
    : undefined

  return (
    <nav className={clsx('lz-nav', { searching: isSearching })} aria-label='Menu principal'>
      <div className='lz-nav-search'>
        <label>
          <KTSVG path={MenuPaths.Busca} className='lz-nav-search-icon' />
          <input
            ref={searchRef}
            type='search'
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder='Buscar no menu'
            aria-label='Buscar no menu'
            aria-keyshortcuts='Control+K Meta+K'
            autoComplete='off'
          />
          <kbd aria-hidden='true'>{isMac ? '⌘ K' : 'Ctrl K'}</kbd>
        </label>
      </div>

      {shownSections.map((section) => (
        <div className='lz-nav-section' key={section.id}>
          <div className='lz-nav-section-title' id={`lz-nav-section-${section.id}`}>
            {section.title}
          </div>
          <ul className='lz-nav-list' aria-labelledby={`lz-nav-section-${section.id}`}>
            {section.entries.map((entry) => {
              if (entry.kind === 'link') {
                const isActive = entry.to === activeTo
                return (
                  <li
                    key={entry.id}
                    onMouseEnter={(event) =>
                      openFlyout(entry.id, event.currentTarget.firstElementChild as HTMLElement)
                    }
                    onMouseLeave={scheduleFlyoutClose}
                  >
                    <Link
                      to={entry.to}
                      className={clsx('lz-nav-link', { active: isActive })}
                      aria-current={isActive ? 'page' : undefined}
                      aria-label={entry.title}
                      onFocus={(event) => openFlyout(entry.id, event.currentTarget)}
                      onBlur={closeFlyout}
                    >
                      <KTSVG path={entry.icon} className='lz-nav-icon' />
                      <span className='lz-nav-label'>{entry.title}</span>
                    </Link>
                  </li>
                )
              }

              const isOpen = isSearching || openGroupId === entry.id
              const isFlyoutOpen = isRail && flyout?.entryId === entry.id
              const subId = `lz-nav-sub-${entry.id}`
              return (
                <li
                  key={entry.id}
                  className={clsx('lz-nav-group', {
                    open: isOpen,
                    'has-active': activeGroupId === entry.id,
                    'flyout-open': isFlyoutOpen,
                  })}
                  onMouseEnter={(event) =>
                    openFlyout(entry.id, event.currentTarget.firstElementChild as HTMLElement)
                  }
                  onMouseLeave={scheduleFlyoutClose}
                >
                  <button
                    type='button'
                    className='lz-nav-link'
                    aria-label={entry.title}
                    aria-expanded={isRail ? isFlyoutOpen : isOpen}
                    aria-controls={isRail ? flyoutDomId(entry.id) : subId}
                    onClick={(event) => handleGroupClick(entry.id, event.currentTarget)}
                    onKeyDown={(event) => {
                      if (isRail && event.key === 'ArrowRight') {
                        event.preventDefault()
                        openFlyout(entry.id, event.currentTarget, true)
                      }
                    }}
                    onBlur={isRail ? handleTriggerBlur : undefined}
                  >
                    <KTSVG path={entry.icon} className='lz-nav-icon' />
                    <span className='lz-nav-label'>{entry.title}</span>
                    <KTSVG path={MenuPaths.Chevron} className='lz-nav-chevron' />
                  </button>
                  <ul className='lz-nav-sub' id={subId}>
                    {entry.items.map((item) => (
                      <NavSubLink
                        key={item.to}
                        to={item.to}
                        title={item.title}
                        isActive={item.to === activeTo}
                      />
                    ))}
                  </ul>
                </li>
              )
            })}
          </ul>
        </div>
      ))}

      <div className='lz-nav-empty' role='status'>
        {isSearching && shownSections.length === 0 ? 'Nenhum item encontrado.' : ''}
      </div>

      {isRail && flyout && flyoutEntry && (
        <AsideNavFlyout
          key={flyoutEntry.id}
          entryId={flyoutEntry.id}
          title={flyoutEntry.title}
          anchor={flyout.anchor}
          items={flyoutEntry.kind === 'group' ? flyoutEntry.items : undefined}
          activeTo={activeTo}
          focusFirst={flyout.focusFirst}
          onMouseEnter={cancelFlyoutClose}
          onMouseLeave={scheduleFlyoutClose}
          onDismiss={dismissFlyout}
          onFocusLeave={closeFlyout}
        />
      )}
    </nav>
  )
}

export { AsideNav }
